import React, { useState } from "react";
import { Trash2, Copy, Download, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { imgUrl } from "../../../lib/api";
import { uploadFile } from "../../../lib/chatUpload";
import { useFb, inp, card, btn, btnMain, copyText, Empty } from "./shared";

const Uploader = ({ onAdd }) => {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const res = await uploadFile(file, { onProgress: setPct });
      await onAdd({ title: file.name.replace(/\.[^.]+$/, ""), url: res.url });
      toast.success("Picture added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setPct(null); }
  };
  return (
    <label className={`${btnMain} cursor-pointer`} data-testid="fb-pic-upload">
      {pct !== null ? <><Loader2 size={15} className="animate-spin" />{pct}%</> : <><Upload size={15} />Upload picture</>}
      <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={pct !== null} data-testid="fb-pic-upload-input" />
    </label>
  );
};

const isCover = (p) => /cover/i.test(p.tags || "");
const isProfile = (p) => /profile/i.test(p.tags || "");
const FILTERS = [["", "All"], ["cover", "Cover photos"], ["photo post", "Photo posts"], ["profile", "Profile pictures"], ["holiday", "Holiday"]];

const PicCard = ({ p, update, remove }) => {
  const f = (k) => ({ value: p[k] ?? "", onChange: (e) => update(p.id, { [k]: e.target.value }, false), onBlur: (e) => update(p.id, { [k]: e.target.value }) });
  return (
    <div className={`${card} space-y-2 ${isCover(p) ? "sm:col-span-2" : ""}`} data-testid={`fb-pic-${p.id}`}>
      <img src={imgUrl(p.url)} alt={p.title} className={`w-full rounded-xl bg-[#f4f8f7] object-cover ${isCover(p) ? "aspect-[1640/624]" : isProfile(p) ? "aspect-square !rounded-full" : /photo/i.test(p.tags || "") ? "aspect-[4/5]" : "aspect-square"}`} />
      <input {...f("title")} className={`${inp} font-semibold`} placeholder="Title" data-testid={`fb-pic-title-${p.id}`} />
      <textarea rows={3} {...f("caption")} className={inp} placeholder="Caption for this picture" data-testid={`fb-pic-caption-${p.id}`} />
      <input {...f("tags")} className={inp} placeholder="Tags (e.g. holiday, tips)" data-testid={`fb-pic-tags-${p.id}`} />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => copyText(p.caption, "Caption copied")} className={btn} data-testid={`fb-pic-copy-${p.id}`}><Copy size={13} />Copy</button>
        <a href={imgUrl(p.url)} download target="_blank" rel="noreferrer" className={btn} data-testid={`fb-pic-download-${p.id}`}><Download size={13} />Download</a>
        <button onClick={() => remove(p.id)} className={`${btn} ml-auto`} data-testid={`fb-pic-delete-${p.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

export default function PicturesSection() {
  const { items, add, update, remove } = useFb("pictures");
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const list = items.filter((p) => (!tag || (p.tags || "").toLowerCase().includes(tag)) && (!q || `${p.title} ${p.tags} ${p.caption}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-4" data-testid="fb-pictures-section">
      <div className={`${card} flex flex-wrap items-center gap-3`}>
        <input value={q} onChange={(e) => setQ(e.target.value)} className={`${inp} max-w-xs`} placeholder="Search pictures…" data-testid="fb-pic-search" />
        <span className="text-[13px] text-[#6b7c79]">{items.length} pictures · photo posts are used by "Plan next 7 days"</span>
        <div className="ml-auto"><Uploader onAdd={add} /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setTag(k)} className={`${btn} ${tag === k ? "!border-[#0e8c7f] !bg-[#e8f8f4] !text-[#0b6f65]" : ""}`} data-testid={`fb-pic-filter-${k ? k.replace(" ", "-") : "all"}`}>
            {l} ({k ? items.filter((p) => (p.tags || "").toLowerCase().includes(k)).length : items.length})
          </button>
        ))}
      </div>
      {tag === "profile" && <p className="text-[12.5px] text-[#6b7c79]">Profile pictures are 1080×1080 and look right inside Facebook's round frame. Upload one under "Edit profile picture".</p>}
      {tag === "cover" && <p className="text-[12.5px] text-[#6b7c79]">Cover photos are 1640×624 — download one and upload it on your Facebook page under "Edit cover photo". Text stays inside the area phones show.</p>}
      {list.length === 0 ? <Empty testid="fb-pic-empty">No pictures yet. Upload one to start.</Empty> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((p) => <PicCard key={p.id} p={p} update={update} remove={remove} />)}
        </div>
      )}
    </div>
  );
}
